export default function initSearch(root = document) {
  const SUGGEST_URL = "https://searchweb.londonmet.ac.uk/s/suggest.json";

  const COLLECTIONS = ["website-meta", "courses", "website"];
  let activeIndex = -1;
  let timeout;

  const input = root.querySelector("#sz-search-input");
  const suggestions = root.querySelector("#sz-search-suggestions");

  if (!input || !suggestions) return;

  let controller;

  /* -----------------------------------------
     AUTOSUGGEST
  ----------------------------------------- */

  input.addEventListener("input", (e) => {
    const query = e.target.value.trim();

    clearTimeout(timeout);

    if (query.length < 2) {
      suggestions.hidden = true;
      input.setAttribute("aria-expanded", "false");
      input.setAttribute("aria-activedescendant", "");
      activeIndex = -1;
      return;
    }

    timeout = setTimeout(() => {
      activeIndex = -1;
      input.setAttribute("aria-activedescendant", "");

      suggestions.hidden = false;
      suggestions.innerHTML = `
        <div class="sz-search-suggestions__loading" role="status" aria-live="polite">
          Searching…
        </div>
      `;

      input.setAttribute("aria-expanded", "true");

      runSuggest(query);
    }, 200);
  });

  /* -----------------------------------------
     CLICK SUGGESTION
  ----------------------------------------- */

  suggestions.addEventListener("click", (e) => {
    const item = e.target.closest(".sz-search-suggestion");
    if (!item) return;

    input.value = item.dataset.value;

    suggestions.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-activedescendant", "");
    activeIndex = -1;

    input.form.submit();
  });

  /* -----------------------------------------
     FETCH SUGGESTIONS
  ----------------------------------------- */

  async function runSuggest(query) {
    if (controller) controller.abort();
    controller = new AbortController();

    try {
      const requests = COLLECTIONS.map((collection) => {
        const url = `${SUGGEST_URL}?collection=${collection}&partial_query=${encodeURIComponent(query)}&show=10&sort=0&alpha=0.5&fmt=json++`;

        return fetch(url, { signal: controller.signal })
          .then((res) => (res.ok ? res.json() : []))
          .catch(() => []);
      });

      const results = await Promise.all(requests);

      if (query !== input.value.trim()) return;

      const merged = mergeSuggestions(results);

      renderSuggestions(merged, suggestions, input);

      const status = document.getElementById("sz-search-status");
      if (status) {
        status.textContent = `${merged.length} suggestions available`;
      }

    } catch (err) {
      if (err.name !== "AbortError") {
        console.warn("Suggest error", err);
      }
    }
  }

  /* -----------------------------------------
     MODAL EVENTS
  ----------------------------------------- */

  document.addEventListener("modal:open", (e) => {
    if (e.detail?.id === "sz-search-modal") {
      input.focus();
    }
  });

  document.addEventListener("modal:close", (e) => {
    if (e.detail?.id !== "sz-search-modal") return;

    suggestions.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-activedescendant", "");
    activeIndex = -1;
  });

  /* -----------------------------------------
     BLUR
  ----------------------------------------- */

  input.addEventListener("blur", () => {
    setTimeout(() => {
      suggestions.hidden = true;
      input.setAttribute("aria-expanded", "false");
      input.setAttribute("aria-activedescendant", "");
      activeIndex = -1;
    }, 150);
  });

  /* -----------------------------------------
     KEYBOARD NAV
  ----------------------------------------- */

  input.addEventListener("keydown", (e) => {
    const items = suggestions.querySelectorAll(".sz-search-suggestion");
    if (!items.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % items.length;
      updateActive(items, activeIndex, input);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      activeIndex = (activeIndex - 1 + items.length) % items.length;
      updateActive(items, activeIndex, input);
    }

    if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        items[activeIndex].click();
      } else if (!input.value.trim()) {
        e.preventDefault();
      }
    }

    if (e.key === "Escape") {
      if (!suggestions.hidden) {
        suggestions.hidden = true;
        input.setAttribute("aria-expanded", "false");
        input.setAttribute("aria-activedescendant", "");
        activeIndex = -1;
      }
      // otherwise let modal close
    }
  });

  /* -----------------------------------------
     MOUSE INTERACTION
  ----------------------------------------- */

  suggestions.addEventListener("mousemove", (e) => {
    const items = suggestions.querySelectorAll(".sz-search-suggestion");
    const item = e.target.closest(".sz-search-suggestion");
    if (!item) return;

    items.forEach((el, i) => {
      const isActive = el === item;

      if (isActive) activeIndex = i;

      el.classList.toggle("is-active", isActive);
      el.setAttribute("aria-selected", isActive ? "true" : "false");

      if (isActive) {
        input.setAttribute("aria-activedescendant", el.id);
      }
    });
  });

  suggestions.addEventListener("mouseleave", () => {
    activeIndex = -1;

    suggestions.querySelectorAll(".sz-search-suggestion").forEach((el) => {
      el.classList.remove("is-active");
      el.setAttribute("aria-selected", "false");
    });

    input.setAttribute("aria-activedescendant", "");
  });
}

/* -----------------------------------------
   MERGE + DEDUPE
----------------------------------------- */

function mergeSuggestions(resultsArrays) {
  const seen = new Set();
  const merged = [];

  resultsArrays.forEach((arr) => {
    if (!Array.isArray(arr)) return;

    arr.forEach((item) => {
      const value = item.disp?.trim();
      if (!value) return;

      const key = value.toLowerCase();

      if (!seen.has(key)) {
        seen.add(key);
        merged.push({ label: value });
      }
    });
  });

  return merged.slice(0, 10);
}

/* -----------------------------------------
   RENDER
----------------------------------------- */

function renderSuggestions(items, suggestions, input) {
  if (!items.length) {
    suggestions.hidden = true;
    input.setAttribute("aria-expanded", "false");
    return;
  }

  suggestions.innerHTML = `
    <ul class="sz-search-suggestions__list">
      ${items.map((item, i) => `
        <li
          id="sz-suggestion-${i}"
          role="option"
          class="sz-search-suggestion"
          data-value="${item.label}"
          aria-selected="false"
          tabindex="-1"
        >
          ${highlightMatch(item.label, input.value)}
        </li>
      `).join("")}
    </ul>
  `;

  suggestions.hidden = false;
  input.setAttribute("aria-expanded", "true");
  input.setAttribute("aria-activedescendant", "");
  activeIndex = -1;
}

/* -----------------------------------------
   ACTIVE STATE
----------------------------------------- */

function updateActive(items, activeIndex, input) {
  items.forEach((el, i) => {
    const isActive = i === activeIndex;

    el.classList.toggle("is-active", isActive);
    el.setAttribute("aria-selected", isActive ? "true" : "false");

    if (isActive) {
      input.setAttribute("aria-activedescendant", el.id);
    }
  });

  const active = items[activeIndex];
  if (active) {
    active.scrollIntoView({ block: "nearest" });
  }
}

/* -----------------------------------------
   HIGHLIGHT
----------------------------------------- */

function highlightMatch(text, query) {
  if (!query) return text;
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${safe})`, "ig");
  return text.replace(regex, "<mark>$1</mark>");
}